import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import LoadingSpinner from '../../../components/nav/LoadingSpinner';
import ContentCoverageDrawer from '../../personalizationManager/OpportunityMap/components/ContentCoverageDrawer/ContentCoverageDrawer';
import styled from 'styled-components';

import aampeTheme from '../../../constants/aampeTheme';
import routes from '../../../constants/routes';

import useGetLocationState from '../../../hooks/helper/useGetLocationState';
import useGetHeroRecommendations from 'hooks/personalization/useGetHeroRecommendations';

import {
    HeroRecommendation,
    PersonalizationTab,
} from '../../../models/personalization.model';

import { SelectedCellLabels } from '../../personalizationManager/OpportunityMap/contants';
import EmptyState from './EmptyState';
import RecommendationListItem from './RecommendationListItem';

const HeroRecommendationsWidget: React.FC = () => {
    const history = useHistory();
    const { getLocationState } = useGetLocationState('Back to Dashboard');

    const {
        data: recommendations = [],
        isLoading,
        isRefetching,
        isError,
    } = useGetHeroRecommendations();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedCell, setSelectedCell] = useState<
        SelectedCellLabels | undefined
    >(undefined);
    const [drawerXAxisComponentId, setDrawerXAxisComponentId] = useState('');
    const [drawerYAxisComponentId, setDrawerYAxisComponentId] = useState('');

    const handleRecommendationClick = (recommendation: HeroRecommendation) => {
        const xAxisComponentId =
            recommendation?.label_x?.component_type_id ?? '';
        const yAxisComponentId =
            recommendation?.label_y?.component_type_id ?? '';
        const xLabelId = recommendation.label_x.id;
        const yLabelId = recommendation.label_y.id;

        if (
            !xAxisComponentId ||
            !yAxisComponentId ||
            Number.isNaN(xLabelId) ||
            Number.isNaN(yLabelId)
        ) {
            return;
        }

        setDrawerXAxisComponentId(xAxisComponentId);
        setDrawerYAxisComponentId(yAxisComponentId);
        setSelectedCell({
            xAxisSelectedLabel: {
                label: recommendation.label_x.name,
                value: xLabelId,
            },
            yAxisSelectedLabel: {
                label: recommendation.label_y.name,
                value: yLabelId,
            },
        });
        setIsDrawerOpen(true);
    };

    const handleViewContentCoverage = () => {
        const params = new URLSearchParams();
        params.set('tab', PersonalizationTab.CONTENT_COVERAGE);

        history.push(
            `${routes.labelManagement}?${params.toString()}`,
            getLocationState(),
        );
    };

    const isEmpty = useMemo<boolean>(() => {
        if (!recommendations?.length) {
            return true;
        }
        return false;
    }, [recommendations]);

    if (isLoading || isRefetching) {
        return <LoadingSpinner styles={{ height: '100px' }} />;
    }

    if (isError) {
        return (
            <ErrorStateWrap>
                <span>Unable to load recommendations</span>
            </ErrorStateWrap>
        );
    }

    return (
        <Container>
            {isEmpty ? (
                <EmptyState />
            ) : (
                <List className="hide-scroll-bar">
                    {recommendations.map((recommendation) => {
                        return (
                            <RecommendationListItem
                                key={`${recommendation.label_x.id}-${recommendation.label_y.id}`}
                                labelNameX={recommendation.label_x.name}
                                labelNameY={recommendation.label_y.name}
                                componentDataKeyX={
                                    recommendation.label_x.variant_type
                                }
                                componentDataKeyY={
                                    recommendation.label_y.variant_type
                                }
                                onClick={() =>
                                    handleRecommendationClick(recommendation)
                                }
                            />
                        );
                    })}
                </List>
            )}

            {!isEmpty && (
                <FooterLink onClick={handleViewContentCoverage}>
                    See content coverage
                </FooterLink>
            )}

            {isDrawerOpen &&
                selectedCell &&
                drawerXAxisComponentId &&
                drawerYAxisComponentId && (
                    <ContentCoverageDrawer
                        isOpen={isDrawerOpen}
                        onClose={() => setIsDrawerOpen(false)}
                        xAxisComponent={drawerXAxisComponentId}
                        yAxisComponent={drawerYAxisComponentId}
                        selectedCellLabels={selectedCell}
                        setSelectedCell={setSelectedCell}
                        filters={{ formula_states: ['Live'] }}
                    />
                )}
        </Container>
    );
};

export default HeroRecommendationsWidget;

export const Container = styled.div(() => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
}));

export const List = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    maxHeight: 395,
    overflowY: 'auto',
    width: `calc(100% + ${aampeTheme.spacing4(12)})`,
    marginLeft: `-${aampeTheme.spacing4(6)}`,
    marginRight: `-${aampeTheme.spacing4(6)}`,
    borderTop: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderBottom: `1px solid ${aampeTheme.colors.bordersWG01}`,
}));

export const ErrorStateWrap = styled.div(() => ({
    height: 260,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: aampeTheme.spacing(1),
    color: aampeTheme.colors.gray700,
    fontSize: aampeTheme.size.font.textMedium,
    border: `1px dashed ${aampeTheme.colors.bordersWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
}));

export const FooterLink = styled.p(() => ({
    border: 'none',
    background: 'transparent',
    color: aampeTheme.colors.solidA01,
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
    textAlign: 'left',
    cursor: 'pointer',
    lineHeight: aampeTheme.size.lineHeight.textSmall,
}));
